// src/mergesafe/registerClientHooks.js
import { listClients, createClient } from '../services/clientService';

const REGISTER_RETRY_MS = 250;
const REGISTER_TIMEOUT_MS = 15000;

function getOnRampApi() {
  if (typeof window === 'undefined') return null;

  const api = window.OnRamp;
  if (!api || typeof api.registerHook !== 'function') {
    return null;
  }

  return api;
}

function normalizeString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeTags(value) {
  if (Array.isArray(value)) {
    return Array.from(
      new Set(
        value
          .map((tag) => normalizeString(tag))
          .filter(Boolean)
      )
    );
  }

  if (typeof value === 'string') {
    return Array.from(
      new Set(
        value
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean)
      )
    );
  }

  return [];
}

function extractItems(result) {
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.items)) return result.items;
  if (Array.isArray(result?.data)) return result.data;
  return [];
}

function clientMatchesQuery(client, query) {
  const q = normalizeString(query).toLowerCase();
  if (!q) return false;

  return (
    client?.name?.toLowerCase().includes(q) ||
    client?.company?.toLowerCase().includes(q) ||
    client?.email?.toLowerCase().includes(q) ||
    client?.phone?.toLowerCase().includes(q)
  );
}

function clientMatchesStatus(client, status) {
  const s = normalizeString(status);
  if (!s || s === 'all') return true;
  return client?.status === s;
}

function clientMatchesTag(client, tag) {
  const t = normalizeString(tag);
  if (!t || t === 'all') return true;

  return Array.isArray(client?.tags)
    ? client.tags.map((x) => String(x)).includes(t)
    : false;
}

function clampLimit(value, fallback = 10, min = 1, max = 50) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, Math.floor(n)));
}

function buildAddress(args = {}) {
  const nested = args.address && typeof args.address === 'object' ? args.address : {};

  return {
    street: normalizeString(nested.street ?? args.address_street),
    city: normalizeString(nested.city ?? args.address_city),
    state: normalizeString(nested.state ?? args.address_state),
    zip: normalizeString(nested.zip ?? args.address_zip),
    country: normalizeString(nested.country ?? args.address_country),
    raw: normalizeString(nested.raw ?? args.address_raw),
  };
}

function hasAtLeastOneContact(payload) {
  return Boolean(
    normalizeString(payload.email) ||
      normalizeString(payload.secondaryEmail) ||
      normalizeString(payload.phone) ||
      normalizeString(payload.secondaryPhone)
  );
}

function buildCreatePayload(args = {}) {
  return {
    name: normalizeString(args.name),
    contactPerson: normalizeString(args.contactPerson),
    email: normalizeString(args.email),
    secondaryEmail: normalizeString(args.secondaryEmail),
    phone: normalizeString(args.phone),
    secondaryPhone: normalizeString(args.secondaryPhone),
    company: normalizeString(args.company),
    industry: normalizeString(args.industry),
    website: normalizeString(args.website),
    status: normalizeString(args.status) || 'lead',
    source: normalizeString(args.source),
    preferredContactMethod: normalizeString(args.preferredContactMethod),
    assignedTo: normalizeString(args.assignedTo) || undefined,
    tags: normalizeTags(args.tags),
    address: buildAddress(args),
  };
}

function buildSearchClientsHook() {
  return {
    id: 'search_clients',
    name: 'Search clients',
    inputs: [
      { name: 'query', type: 'string' },
      { name: 'status', type: 'string' },
      { name: 'tag', type: 'string' },
      { name: 'limit', type: 'number' },
    ],
    handler: async (args = {}) => {
      const invocationId = `search_clients:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;
      const startedAt = Date.now();

      console.log('[MergeSafe][Hook] search_clients invoked', {
        invocationId,
        startedAtIso: new Date(startedAt).toISOString(),
        args,
      });

      const query = normalizeString(args.query);
      const status = normalizeString(args.status);
      const tag = normalizeString(args.tag);
      const limit = clampLimit(args.limit, 10, 1, 50);

      if (!query) {
        console.warn('[MergeSafe][Hook] search_clients missing required query', {
          invocationId,
          args,
        });
        throw new Error('search_clients requires a non-empty "query" string.');
      }

      const result = await listClients({
        page: 1,
        limit: 200,
        sortBy: 'name',
        sortOrder: 'asc',
      });

      const items = extractItems(result);

      const matches = items
        .filter((client) => clientMatchesQuery(client, query))
        .filter((client) => clientMatchesStatus(client, status))
        .filter((client) => clientMatchesTag(client, tag))
        .slice(0, limit);

      const response = {
        query,
        totalMatches: matches.length,
        items: matches,
      };

      console.log('[MergeSafe][Hook] search_clients completed', {
        invocationId,
        durationMs: Date.now() - startedAt,
        totalFetched: items.length,
        totalMatches: matches.length,
      });

      return response;
    },
  };
}

function buildCreateClientHook() {
  return {
    id: 'create_client',
    name: 'Create client',
    inputs: [
      { name: 'name', type: 'string' },
      { name: 'contactPerson', type: 'string' },
      { name: 'email', type: 'string' },
      { name: 'secondaryEmail', type: 'string' },
      { name: 'phone', type: 'string' },
      { name: 'secondaryPhone', type: 'string' },
      { name: 'company', type: 'string' },
      { name: 'industry', type: 'string' },
      { name: 'website', type: 'string' },
      { name: 'status', type: 'string' },
      { name: 'source', type: 'string' },
      { name: 'preferredContactMethod', type: 'string' },
      { name: 'assignedTo', type: 'string' },
      { name: 'tags', type: 'array' },
      { name: 'address', type: 'object' },
      { name: 'address_street', type: 'string' },
      { name: 'address_city', type: 'string' },
      { name: 'address_state', type: 'string' },
      { name: 'address_zip', type: 'string' },
      { name: 'address_country', type: 'string' },
      { name: 'address_raw', type: 'string' },
    ],
    handler: async (args = {}) => {
      const invocationId = `create_client:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;
      const startedAt = Date.now();

      console.log('[MergeSafe][Hook] create_client invoked', {
        invocationId,
        startedAtIso: new Date(startedAt).toISOString(),
        args,
      });

      const payload = buildCreatePayload(args);

      console.log('[MergeSafe][Hook] create_client normalized payload', {
        invocationId,
        payload,
      });

      if (!payload.name) {
        console.warn('[MergeSafe][Hook] create_client missing required name', {
          invocationId,
          args,
          payload,
        });
        throw new Error('create_client requires a non-empty "name" field.');
      }

      if (!hasAtLeastOneContact(payload)) {
        console.warn('[MergeSafe][Hook] create_client missing contact method', {
          invocationId,
          args,
          payload,
        });
        throw new Error(
          'create_client requires at least one contact method: email, secondaryEmail, phone, or secondaryPhone.'
        );
      }

      const response = await createClient(payload);

      console.log('[MergeSafe][Hook] create_client completed', {
        invocationId,
        durationMs: Date.now() - startedAt,
        response,
      });

      return response;
    },
  };
}

function registerHooksIntoOnRamp(onRamp) {
  console.log('[MergeSafe][Hook] registerHooksIntoOnRamp start', {
    ts: Date.now(),
    hasListHooks: typeof onRamp.listHooks === 'function',
    hasRegisterHook: typeof onRamp.registerHook === 'function',
  });

  onRamp.registerHook(buildSearchClientsHook());
  onRamp.registerHook(buildCreateClientHook());

  const hooks = typeof onRamp.listHooks === 'function' ? onRamp.listHooks() : [];

  console.log(
    '[MergeSafe][Hook] Registered OnRamp hooks:',
    hooks.map((hook) => `${hook.id}:${hook.source}`)
  );
}

export function registerClientHooks() {
  if (typeof window === 'undefined') return () => {};

  let disposed = false;
  let registered = false;
  let intervalId = null;
  let timeoutId = null;

  const tryRegister = () => {
    console.log('[MergeSafe][Hook] tryRegister called', {
      disposed,
      registered,
      ts: Date.now(),
    });

    if (disposed || registered) return;

    const onRamp = getOnRampApi();

    if (!onRamp) {
      console.log('[MergeSafe][Hook] OnRamp API not ready yet', {
        ts: Date.now(),
        hasWindowOnRamp: Boolean(window.OnRamp),
      });
      return;
    }

    registerHooksIntoOnRamp(onRamp);
    registered = true;

    console.log('[MergeSafe][Hook] registration complete', {
      ts: Date.now(),
    });

    if (intervalId) {
      window.clearInterval(intervalId);
      intervalId = null;
    }

    if (timeoutId) {
      window.clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  tryRegister();

  if (!registered) {
    console.log('[MergeSafe][Hook] starting retry interval', {
      retryMs: REGISTER_RETRY_MS,
      timeoutMs: REGISTER_TIMEOUT_MS,
      ts: Date.now(),
    });

    intervalId = window.setInterval(tryRegister, REGISTER_RETRY_MS);

    timeoutId = window.setTimeout(() => {
      if (!registered && process.env.NODE_ENV !== 'production') {
        console.warn(
          '[MergeSafe][Hook] window.OnRamp.registerHook was not available before timeout; client hooks were not registered yet.'
        );
      }

      if (intervalId) {
        window.clearInterval(intervalId);
        intervalId = null;
      }

      timeoutId = null;
    }, REGISTER_TIMEOUT_MS);
  }

  return () => {
    disposed = true;

    console.log('[MergeSafe][Hook] registerClientHooks cleanup', {
      ts: Date.now(),
    });

    if (intervalId) {
      window.clearInterval(intervalId);
      intervalId = null;
    }

    if (timeoutId) {
      window.clearTimeout(timeoutId);
      timeoutId = null;
    }
  };
}